import { castToArray } from "@lyfegen-platform/utils";
import { BoxProps } from "@material-ui/core";
import { LegacyRef } from "react";

type LiteBoxProps = Pick<
	BoxProps,
	| "id"
	| "display"
	| "justifyContent"
	| "alignItems"
	| "p"
	| "pb"
	| "px"
	| "py"
	| "pt"
	| "pl"
	| "pr"
	| "mx"
	| "my"
	| "ml"
	| "mr"
	| "mb"
	| "mt"
	| "height"
	| "width"
	| "minWidth"
	| "maxWidth"
	| "padding"
	| "margin"
	| "marginBottom"
	| "marginRight"
	| "marginLeft"
	| "marginTop"
	| "paddingBottom"
	| "paddingRight"
	| "paddingLeft"
	| "paddingTop"
	| "borderLeft"
	| "borderRight"
	| "borderTop"
	| "borderBottom"
	| "position"
	| "flexDirection"
	| "flexWrap"
	| "flexGrow"
	| "flexBasis"
	| "flexShrink"
	| "hidden"
	| "minHeight"
	| "maxHeight"
	| "whiteSpace"
	| "borderRadius"
	| "color"
> & {
	className?: string;
	gap?: number | string;
	cursor?: string;
	backgroundColor?: string;
	onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	onMouseOver?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	onMouseOut?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	onMouseEnter?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	onMouseLeave?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	forwardedRef?: LegacyRef<HTMLDivElement>;
};

const mapStyleProperties = (
	property:
		| "p"
		| "pb"
		| "px"
		| "py"
		| "pt"
		| "pl"
		| "pr"
		| "mx"
		| "my"
		| "ml"
		| "mr"
		| "mb"
		| "mt"
		| "padding"
		| "margin"
		| "marginBottom"
		| "marginRight"
		| "marginLeft"
		| "marginTop"
		| "paddingBottom"
		| "paddingRight"
		| "paddingLeft"
		| "paddingTop"
		| "flexGrow"
		| "flexBasis"
): string | Array<string> | undefined => {
	switch (property) {
		case "p":
			return "padding";
		case "pb":
			return "paddingBottom";
		case "px":
			return ["paddingLeft", "paddingRight"];
		case "py":
			return ["paddingTop", "paddingBottom"];
		case "pt":
			return "paddingTop";
		case "pl":
			return "paddingLeft";
		case "pr":
			return "paddingRight";
		case "mx":
			return ["marginLeft", "marginRight"];
		case "my":
			return ["marginTop", "marginBottom"];
		case "ml":
			return "marginLeft";
		case "mr":
			return "marginRight";
		case "mb":
			return "marginBottom";
		case "mt":
			return "marginTop";
		case "padding":
		case "margin":
		case "marginBottom":
		case "marginRight":
		case "marginLeft":
		case "marginTop":
		case "paddingBottom":
		case "paddingRight":
		case "paddingLeft":
		case "paddingTop":
		case "flexGrow":
		case "flexBasis":
			return property;
	}
};

export const LiteBox: React.FC<LiteBoxProps> = ({
	children,
	className,
	onClick,
	onMouseOver,
	onMouseOut,
	onMouseEnter,
	onMouseLeave,
	forwardedRef,
	hidden,
	...other
}) => {
	const mappedStyles = Object.entries(other).flatMap(([key, value]) => {
		const mappedProperty = mapStyleProperties(key as any);
		if (!mappedProperty) {
			return [[key, value]];
		}

		return castToArray(mappedProperty).map((rule) => [rule, typeof value === "number" ? value * 8 : value]);
	});

	return (
		<div
			className={className}
			style={{ ...Object.fromEntries(mappedStyles) }}
			onClick={onClick}
			onMouseOver={onMouseOver}
			onMouseOut={onMouseOut}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			ref={forwardedRef}
			hidden={hidden}
		>
			{children}
		</div>
	);
};
